// 🛡️ B223-FU (AI_116) — REDE DE FIDELIDADE NOS DADOS-FONTE da análise geral.
// ─────────────────────────────────────────────────────────────────────────────
// Mapeia os dados-fonte (já coletados/anonimizados NO FRONT) para o contrato do
// detectorContraditorios, roda o detector (puro, sem IA) e devolve, de forma
// CONSERVADORA e NÃO-BLOQUEANTE, no máximo UM AvisoFidelidade consolidado para a
// caixa "Pontos a conferir" da análise geral (B223). Roda no navegador → ZERO
// dado novo do paciente em trânsito (LGPD), com o sinal completo (texto não
// truncado pela minimização do cruzamento).
//
// Contexto da análise geral: NÃO existe "biópsia atual" (biopsiaAtualId = null) —
// o detector serve aqui só como cruzamento dos sinais de hepatopatia avançada /
// hipertensão portal presentes nos exames-fonte, para o médico conferir se a
// análise os contemplou.
import { DadosAnonimizados, AvisoFidelidade, ImagemAnonimizada } from "../types";
import {
  avaliarContexto,
  normalizar,
  temNegacaoAntes,
  CtxContraditorio,
} from "./detectorContraditorios";

const TIPO_AVISO = "contradicao_fonte";
const MAX_ITENS = 6; // não inundar a caixa "Pontos a conferir"

// Classificação do exame de imagem por papel do laudista (preferencial) ou pelo
// tipo/descrição (fallback). Endoscopia → gatilho EDA; biópsia/anatomopatológico
// → gatilho biópsia; demais (US/TC/RM/elastografia) → gatilho imagem.
function ehEndoscopia(img: ImagemAnonimizada): boolean {
  if (img.achados?.tipoLaudista === "endoscopista") return true;
  const t = normalizar(`${img.tipo || ""} ${img.descricao || ""}`);
  return /endoscop|\beda\b|\begd\b/.test(t);
}
function ehBiopsia(img: ImagemAnonimizada): boolean {
  if (img.achados?.tipoLaudista === "patologista") return true;
  const t = normalizar(`${img.tipo || ""} ${img.descricao || ""}`);
  return /bi[oó]psia|anatomopatol|histopatol|explante/.test(t);
}

// Valor de plaquetas a partir do mapa `valores` do exame anonimizado (a chave é
// "Plaquetas", já em /µL via normalizarContagemMilhar). Tolera string/numero.
function extrairPlaquetas(valores: Record<string, number | string> | undefined): number | null {
  if (!valores) return null;
  for (const [k, v] of Object.entries(valores)) {
    if (normalizar(k).startsWith("plaqueta")) {
      if (typeof v === "number") return isFinite(v) ? v : null;
      // string: remove separador de milhar e troca vírgula decimal
      const n = parseFloat(String(v).replace(/\.(?=\d{3}\b)/g, "").replace(",", "."));
      return isFinite(n) ? n : null;
    }
  }
  return null;
}

// Monta o ctx do detector a partir dos dados anonimizados da análise geral.
export function montarContextoContraditorio(dados: DadosAnonimizados): CtxContraditorio {
  const imagens = dados.imagens || [];
  const examesImagem: any[] = [];
  const edas: any[] = [];
  const outrasBiopsias: any[] = [];

  for (const img of imagens) {
    const item = {
      id: img.id,
      laudo: img.laudo,
      analiseCompleta: img.resumoAchados,
      achados: img.achados,
      tipoExame: img.tipo,
      dataExame: img.data,
    };
    if (ehBiopsia(img)) outrasBiopsias.push(item);
    else if (ehEndoscopia(img)) edas.push(item);
    else examesImagem.push(item);
  }

  const examesLaboratoriais = (dados.examesLab || [])
    .map((e) => ({ plaquetas: extrairPlaquetas(e.valores), data: e.data }))
    .filter((e) => e.plaquetas != null);

  return {
    biopsiaAtualId: null, // análise geral não tem "biópsia atual"
    examesImagem,
    outrasBiopsias,
    citacoesHistopato: [],
    edas,
    examesLaboratoriais,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// B260 (AI_117) — DE CHECKLIST DE CONFIRMAÇÃO → DETECTOR DE OMISSÃO.
// Cruza cada sinal detectado na FONTE com o TEXTO da análise e mantém só o que a
// análise NÃO contemplou. Determinístico, sem IA. Cada sinal detectado é mapeado
// a um CONCEITO clínico com sinônimos; o conceito é "coberto" só por menção
// AFIRMATIVA (reusa a guarda de negação testada do detector) — se a análise só
// NEGA um sinal que a fonte mostra, isso é CONTRADIÇÃO e segue sinalizado.
// ─────────────────────────────────────────────────────────────────────────────
interface ConceitoCobertura {
  // casa o RÓTULO normalizado do achado detectado (vindo do detector)
  casaRotulo: RegExp;
  // sinônimos do conceito a procurar no TEXTO (normalizado) da análise
  sinonimos: RegExp;
}

// Ordem importa: o mais específico vem antes (varizes gástricas antes de varizes
// genéricas). Rótulos e sinônimos são testados já normalizados (sem acento,
// minúsculas) — por isso "baço"→"baco", "cirrose"→"cirrose", "ç"→"c".
const CONCEITOS: ConceitoCobertura[] = [
  // Ascite (imagem)
  { casaRotulo: /ascite/, sinonimos: /ascit/ },
  // Esplenomegalia / hiperesplenismo
  {
    casaRotulo: /esplenomegalia/,
    sinonimos: /esplenomegal|hiperesplen|baco\s+(aumentad|volumos)/,
  },
  // Varizes GÁSTRICAS (específico — manejo próprio) antes das genéricas
  {
    casaRotulo: /varizes\s+gastric/,
    sinonimos: /varize[^.]{0,30}gastric|gastric[^.]{0,30}varize/,
  },
  // Varizes esofágicas / em imagem (qualquer menção a varizes cobre), EXCETO
  // varizes periféricas (membros inferiores/perianais/safena) — não são HP.
  {
    casaRotulo: /varize/,
    sinonimos:
      /varize(?![^.]{0,30}(membro|inferior|perian|perineal|hemorroid|vulvar|escrotal|safen))|varicos(?![^.]{0,20}(perian|perineal|vulvar|escrotal|safen|membro|inferior))/,
  },
  // Gastropatia hipertensiva portal (achado endoscópico específico)
  {
    casaRotulo: /gastropatia/,
    sinonimos: /gastropatia\s+(hipertensiva|congestiva|portal)|\bghp\b/,
  },
  // Sinais radiológicos de hipertensão portal (HP textual, circulação colateral,
  // colaterais/shunts portossistêmicos, recanalização da veia umbilical) →
  // contemplados se a análise nomeia hipertensão portal ou seus sinais.
  {
    casaRotulo:
      /hipertensao\s+portal|circulacao\s+colateral|colaterais\s+portossist|shunts?\s+portossist|recanalizacao/,
    // `colatera` cru casava "efeito/dano colateral" (ubíquo no pós-Tx) →
    // ancorado a contexto vascular/portal. `veia umbilical` exige recanalização.
    sinonimos:
      /hipertensao\s+portal|\bhpcs\b|circulacao\s+colateral|(vasos|veias|circulacao)\s+colatera|colaterais?\s+(porto|portossist|venos|abdominal|periport|periespl|paraumbilic|para[\s-]?umbilic|mesenteric|esplenorrenal|gastric)|shunt[^.]{0,20}portossist|recanalizacao[^.]{0,20}umbilical|veia\s+umbilical[^.]{0,20}(recanaliz|reperme|patent|perv)/,
  },
  // Cirrose / hepatopatia crônica avançada / contornos hepáticos irregulares
  {
    casaRotulo: /cirrose|hepatopatia\s+cronica\s+avancada|contornos/,
    sinonimos:
      /cirro|hepatopatia\s+cronica\s+avancada|\bdhca\b|contornos?[^.]{0,20}(irregular|lobulad|nodular|bossel|rombud)|superficie[^.]{0,20}(irregular|nodular|lobulad)|figado[^.]{0,15}nodular/,
  },
  // Fibrose avançada por elastografia OU biópsia (METAVIR/Ishak/F3-F4/kPa).
  // Sinônimos só de ACHADO: tokens crus de procedimento/modalidade ("biopsia",
  // "elastografia") e genéricos ("fibrose", "f3/f4" soltos) foram removidos —
  // casavam recomendação ("aguardando biópsia"), outro órgão ("fibrose pulmonar")
  // ou outra classificação e ESCONDIAM uma fibrose hepática confirmada na fonte.
  {
    casaRotulo: /elastografia|biopsia\s+|metavir|ishak/,
    sinonimos:
      /metavir|ishak|fibroscan|\barfi\b|\bkpa\b|cirro|fibrose\s+(hepatic|avancad|significativ|septal|em\s+ponte|portal|periport|grau|estagio|f[0-4])/,
  },
  // Plaquetopenia persistente. Cobertura LARGA de propósito (sinal de menor peso
  // clínico): basta a análise citar as PLAQUETAS (o valor) ou o hiperesplenismo
  // — se a análise olhou as plaquetas, não houve omissão. (AI_117: corrige falso
  // positivo ao vivo — "plaquetas 130.000, em tendência favorável" não casava.)
  {
    casaRotulo: /plaquetopenia/,
    sinonimos: /plaquetopenia|trombocitopenia|plaqueta|hiperesplen/,
  },
];

// Negação PÓS-posicional na MESMA cláusula (para no 1º terminador . , ; \n após
// o match): "ascite ausente", "varizes não visualizadas", "cirrose descartada".
// O `temNegacaoAntes` só olha p/ trás → sem isto, um sinal NEGADO depois contava
// como cobertura e silenciava uma CONTRADIÇÃO (fonte mostra × análise nega).
const NEG_DEPOIS =
  /\b(ausentes?|ausencia|negativ|descartad|afastad|excluid|nao\s+(identificad|visualizad|observ|caracteriz|evidenciad|detectad|demonstrad|ha|foi|foram))/;
function temNegacaoDepois(
  textoNorm: string,
  fimMatch: number,
  re: RegExp = NEG_DEPOIS
): boolean {
  let resto = textoNorm.substring(fimMatch);
  const t = resto.search(/[.,;\n]/);
  if (t >= 0) resto = resto.substring(0, t);
  return re.test(resto);
}

// Há ALGUMA menção AFIRMATIVA (não negada antes NEM depois) do conceito no texto?
// `negDepoisRe` permite uma negação pós-posicional específica do domínio (B265:
// sorologia "não reagente"/"indetectável" não é coberta pelo NEG_DEPOIS de HP).
function mencaoAfirmativa(
  textoNorm: string,
  sinonimos: RegExp,
  negDepoisRe: RegExp = NEG_DEPOIS
): boolean {
  const re = new RegExp(
    sinonimos.source,
    sinonimos.flags.includes("g") ? sinonimos.flags : sinonimos.flags + "g"
  );
  let m: RegExpExecArray | null;
  while ((m = re.exec(textoNorm)) !== null) {
    const fim = m.index + m[0].length;
    if (
      !temNegacaoAntes(textoNorm, m.index) &&
      !temNegacaoDepois(textoNorm, fim, negDepoisRe)
    ) {
      return true;
    }
    if (m.index === re.lastIndex) re.lastIndex++; // guarda anti-loop (match vazio)
  }
  return false;
}

// O conceito do sinal já está CONTEMPLADO (afirmativamente) na análise?
export function conceitoCobertoNaAnalise(rotulo: string, textoAnaliseNorm: string): boolean {
  if (!textoAnaliseNorm) return false; // sem texto → nada coberto (= comportamento B223-FU)
  const rotuloNorm = normalizar(rotulo);
  const conceito = CONCEITOS.find((c) => c.casaRotulo.test(rotuloNorm));
  if (!conceito) return false; // achado sem conceito mapeado → NÃO esconde (conservador)
  return mencaoAfirmativa(textoAnaliseNorm, conceito.sinonimos);
}

// Achata um objeto de análise estruturada (AnaliseIA) em texto pesquisável,
// coletando só os VALORES (nunca os nomes de campo, p/ não casar sinônimo numa
// chave). Usado p/ o cruzamento de omissão do B260.
export function extrairTextoAnalise(no: unknown): string {
  if (no == null) return "";
  if (typeof no === "string") return no;
  if (typeof no === "number" || typeof no === "boolean") return String(no);
  if (Array.isArray(no)) return no.map(extrairTextoAnalise).join(" ");
  if (typeof no === "object") {
    return Object.values(no as Record<string, unknown>)
      .map(extrairTextoAnalise)
      .join(" ");
  }
  return "";
}

// ─────────────────────────────────────────────────────────────────────────────
// B265 (AI_118) — BACKSTOP DETERMINÍSTICO DE OMISSÃO DE HBV (medicação/conduta).
// A varredura AI_118 triangulou (JMIR/PMID 42114040 + arXiv 2512.04118) que o
// ponto fraco da IA clínica é MEDICAÇÃO/CONDUTA. O eixo de geração já é coberto
// pelo B259 (anti-HBV calibrado pelo risco, no system prompt); este é a REDE
// determinística que pega quando o modelo ESQUECE o HBV apesar do B259. Mesma
// postura do B260: front-only, sem IA, sem dado novo em trânsito, não-bloqueante.
// Curadoria clínica (Leonardo, AI_118): HBsAg+/DNA-VHB detectável dispara SEMPRE;
// anti-HBc reagente isolado dispara SÓ em transplante/imunossupressão (onde a
// reativação é o risco real). Cobertura na análise reusa a guarda de negação do
// B260 → "hepatite B descartada" não conta como cobertura (é contradição).
// ─────────────────────────────────────────────────────────────────────────────
const TIPO_AVISO_HBV = "omissao_hbv";

// Sorologia qualitativa REAGENTE (normalizada "Reagente"; tolera positivo cru).
function ehReagente(v: string | undefined): boolean {
  if (!v) return false;
  const n = normalizar(v);
  if (n.includes("nao reagente") || n.startsWith("nao ") || n.includes("indetermin"))
    return false;
  return n.includes("reagente") || /\b(reativ|positiv)/.test(n);
}

// Carga viral DETECTÁVEL: número >0, ou "Detectável (...)"; exclui "Não detectado".
function ehCargaDetectavel(v: string | undefined): boolean {
  if (!v) return false;
  const n = normalizar(v);
  if (n.includes("nao detect") || n.includes("indetect")) return false;
  if (n.includes("detect")) return true; // "detectavel (nao quantificavel)"
  const num = parseFloat(n.replace(/\.(?=\d{3}\b)/g, "").replace(",", "."));
  return isFinite(num) && num > 0;
}

// Contexto de imunossupressão / transplante (anti-HBc isolado só importa aqui).
function ehContextoImunossupressao(dados: DadosAnonimizados): boolean {
  if (dados.transplante) return true;
  if (Array.isArray(dados.dadosTransplante) && dados.dadosTransplante.length > 0)
    return true;
  const med = normalizar(dados.medicacoesEmUso || "");
  return /tacrolimo|tacrolimus|ciclosporin|everolimo|sirolimo|rapamicina|micofenolat|\bmmf\b|azatioprina|prednison|metilprednisolon|rituximab|timoglobulina|basiliximab/.test(
    med
  );
}

// HBV mencionado AFIRMATIVAMENTE na análise (reusa negação antes/depois do B260).
const HBV_SINONIMOS =
  /hepatite\s*b\b|\bhbv\b|\bvhb\b|\bhbsag\b|hbeag|anti[\s-]?hb[cse]|entecavir|tenofovir|\btdf\b|\btaf\b|lamivudina|\betv\b/;

// Negação pós-posicional ESPECÍFICA de sorologia: "HBsAg não reagente",
// "indetectável", "negativo" — não cobertas pelo NEG_DEPOIS de HP. Se a análise
// NEGA o HBV que a fonte mostra positivo, é contradição → segue sinalizado.
const NEG_DEPOIS_HBV =
  /\b(ausentes?|ausencia|negativ|descartad|afastad|excluid|indetect|nao\s+(reagent|reativ|detect|identificad|evidenciad|ha|foi|foram))/;

// Aviso de omissão de HBV (ou null). textoNorm já vem normalizado pelo chamador.
export function avisoOmissaoHBV(
  dados: DadosAnonimizados,
  textoNorm: string
): AvisoFidelidade | null {
  let hbsagReag = false;
  let dnaDetect = false;
  let antiHBcReag = false;
  for (const s of dados.sorologias || []) {
    if (ehReagente(s.hbsag)) hbsagReag = true;
    if (ehCargaDetectavel(s.pcrVHB)) dnaDetect = true;
    if (ehReagente(s.antiHBcIgG) || ehReagente(s.antiHBcIgM)) antiHBcReag = true;
  }
  const hbvAtivo = hbsagReag || dnaDetect;

  // Decide se há gatilho (independe da cobertura ainda).
  let base: string | null = null;
  if (hbvAtivo) {
    base =
      "Sorologia de hepatite B ativa (HBsAg reagente e/ou DNA-VHB detectável) nos " +
      "exames-fonte que a análise NÃO mencionou — confira indicação de antiviral/" +
      "profilaxia (reativação sob imunossupressão é grave).";
  } else if (antiHBcReag && ehContextoImunossupressao(dados)) {
    base =
      "Anti-HBc reagente (exposição prévia ao HBV) em paciente sob imunossupressão/" +
      "transplante que a análise NÃO mencionou — confira risco de reativação e " +
      "necessidade de profilaxia antiviral.";
  }
  if (!base) return null;

  // Coberto afirmativamente na análise? (negação = contradição → segue sinalizado)
  if (mencaoAfirmativa(textoNorm, HBV_SINONIMOS, NEG_DEPOIS_HBV)) return null;

  return { tipo: TIPO_AVISO_HBV, mensagem: base };
}

// Detector → avisos consolidados p/ a caixa "Pontos a conferir". CONSERVADOR:
// descarta a única moderada de IMAGEM ("baço aumentado por medida", a mais
// ruidosa); mantém os achados fortes + varizes de EDA + plaquetopenia. Dedup por
// rótulo. B260 (AI_117): cruza com o TEXTO da análise e lista só o que ela OMITIU.
// B265 (AI_118): acrescenta o backstop de omissão de HBV (medicação/conduta).
export function avisosContradicaoFonte(
  dados: DadosAnonimizados,
  textoAnalise: string = ""
): AvisoFidelidade[] {
  const avisos: AvisoFidelidade[] = [];
  // Com texto vazio, cai no comportamento B223-FU (lista tudo / não suprime).
  const textoNorm = normalizar(textoAnalise);

  // (1) B260 — sinais de hepatopatia avançada / hipertensão portal omitidos.
  const r = avaliarContexto(montarContextoContraditorio(dados));
  const relevantes = r.contraditorios.filter(
    (c) => !(c.gatilho === 1 && c.severidade === "moderada")
  );
  if (relevantes.length > 0) {
    const rotulosUnicos = Array.from(new Set(relevantes.map((c) => c.rotulo)));
    const omitidos = rotulosUnicos.filter(
      (rotulo) => !conceitoCobertoNaAnalise(rotulo, textoNorm)
    );
    if (omitidos.length > 0) {
      const itens = omitidos.slice(0, MAX_ITENS);
      avisos.push({
        tipo: TIPO_AVISO,
        mensagem:
          "Sinais de hepatopatia avançada / hipertensão portal nos exames-fonte que " +
          "a análise NÃO mencionou — confira: " +
          itens.join("; ") +
          ".",
      });
    }
  }

  // (2) B265 — omissão de HBV (medicação/conduta).
  const hbv = avisoOmissaoHBV(dados, textoNorm);
  if (hbv) avisos.push(hbv);

  return avisos;
}
